/**
 * FX.js — the effects system.
 * owner: fx
 *
 * Implements the frozen `ctx.fx` API and drives everything from the event bus.
 * Nothing here allocates a mesh per particle: all particles live in GPU float
 * textures (ParticleSystem.js), all decals live in one instanced buffer
 * (Decals.js), and every texture is rendered procedurally at boot
 * (Textures.js).
 *
 * Draw-call budget, heavy combat, ultra:
 *   alpha pool   1 update + 1 inject + up to 7 amortised sort passes + 1 draw
 *   additive     1 update + 1 inject + 1 draw
 *   decals       1
 *   tracers      1
 *   muzzle       1 (world) + 1 (viewScene)
 *   shells       1
 *   light shafts 1 per shaft (0 by default)
 *   -> 19–21 draws. The depth+normal prepass is one extra scene traversal and
 *      is skipped entirely once ctx.render publishes `depthNormalTexture`.
 *
 * Everything is seeded: FX never calls Math.random().
 */
import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';
import { registerShot, look } from '../core/Shots.js';

import { buildFXTextures, DECAL_TILE } from './Textures.js';
import { buildTypeTextures, TYPE_INDEX, TYPES, POOL, RECIPES, DEFAULT_SURFACE } from './Presets.js';
import { ParticlePool } from './ParticleSystem.js';
import { DecalSystem } from './Decals.js';
import { DepthCapture, FAR_SENTINEL } from './DepthCapture.js';
import { MuzzleFlash } from './Muzzle.js';
import { TracerSystem } from './Tracers.js';
import { ShellSystem } from './Shells.js';
import { LightPool } from './Lights.js';
import { LightShaft } from './Ambience.js';
// read-only use of the level team's geometry foundry (never mutated, never edited)
import { chamferBox, chamferCyl } from '../world/Geo.js';

const QUALITY_SCALE = { low: 0.35, medium: 0.6, high: 0.85, ultra: 1.0 };
const POOL_SIZE = { low: 4096, medium: 8192, high: 8192, ultra: 16384 };

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _tan = new THREE.Vector3();
const _bit = new THREE.Vector3();
const _n = new THREE.Vector3();
const _m3 = new THREE.Matrix3();
const _col = new THREE.Color();

export function createFXSystem() {
  return new FXSystem();
}

class FXSystem {
  constructor() {
    this.name = 'fx';
    this.priority = 80;
    this.enabled = true;
    this.time = 0;
    this._lightRefresh = 0;
  }

  /* ---------------------------------------------------------------- *
   * init
   * ---------------------------------------------------------------- */
  init(ctx) {
    this.ctx = ctx;
    const renderer = ctx.renderer;
    const q = ctx.quality || 'ultra';
    this.qscale = QUALITY_SCALE[q] ?? 1;

    /* deterministic generators, one per emitter family */
    this.rng = {};
    for (const k of Object.keys(RESEED)) this.rng[k] = makeRng(RESEED[k]);

    /* which pool each particle type belongs to */
    this._addTypes = new Set();
    for (const k of Object.keys(TYPES)) if (TYPES[k].pool === POOL.ADD) this._addTypes.add(k);

    /* GPU-generated atlases */
    this.textures = buildFXTextures(renderer, q);
    if (ctx.materials?.blueNoise?.isTexture) this.textures.blueNoise = ctx.materials.blueNoise;
    this.types = buildTypeTextures();
    const res = { textures: this.textures, types: this.types };
    this.res = res;
    this.atlasTexel = 1 / (q === 'low' ? 1024 : 2048);

    /* scene roots — everything FX owns lives under these so the depth prepass
       can hide them in one operation */
    this.root = new THREE.Group();
    this.root.name = 'fx';
    this.root.matrixAutoUpdate = false;
    ctx.scene.add(this.root);

    this.viewRoot = new THREE.Group();
    this.viewRoot.name = 'fx_view';
    this.viewRoot.matrixAutoUpdate = false;
    ctx.viewScene.add(this.viewRoot);

    /* pools */
    const size = POOL_SIZE[q] ?? 8192;
    this.alphaPool = new ParticlePool(renderer, res, {
      name: 'alpha', size, sorted: true, additive: false,
      maxSpawnPerFrame: 2048, sortPassesPerFrame: q === 'low' ? 3 : 7,
    });
    this.addPool = new ParticlePool(renderer, res, {
      name: 'additive', size: Math.max(4096, size >> 1), sorted: false, additive: true,
      maxSpawnPerFrame: 2048,
    });
    this.root.add(this.alphaPool.mesh, this.addPool.mesh);

    /* decals */
    this.decals = new DecalSystem(res, { capacity: q === 'low' ? 192 : 512 });
    this.root.add(this.decals.mesh);

    /* tracers, muzzle flashes, shells */
    this.tracers = new TracerSystem(96);
    this.root.add(this.tracers.mesh);

    this.muzzleWorld = new MuzzleFlash(8);
    this.root.add(this.muzzleWorld.mesh);
    this.muzzleView = new MuzzleFlash(4);
    this.viewRoot.add(this.muzzleView.mesh);

    this.shells = new ShellSystem(ctx, 32);
    this.root.add(this.shells.mesh);

    /* dynamic lights */
    this.lights = new LightPool(ctx.scene, 4);
    this.viewLights = new LightPool(ctx.viewScene, 1);

    /* depth + normals */
    this.depth = new DepthCapture(renderer);
    this.depth.exclude = [this.root];

    /* light shafts (ambience) */
    this.shafts = [];

    /* shockwaves published for the render team */
    this.shockwaves = [];

    /* environment block handed to every subsystem each frame */
    this.env = {
      camera: ctx.camera,
      depthTexture: null,
      normalTexture: null,
      depthMode: 0,
      farSentinel: FAR_SENTINEL,
      resolution: new THREE.Vector2(1, 1),
      time: 0,
      sunDirWorld: new THREE.Vector3(0.4, 0.65, 0.35).normalize(),
      sunDirView: new THREE.Vector3(0, 1, 0),
      sunColor: new THREE.Color(1.0, 0.94, 0.86),
      ambSky: new THREE.Color(0.18, 0.22, 0.27),
      ambGround: new THREE.Color(0.07, 0.06, 0.05),
      upView: new THREE.Vector3(0, 1, 0),
      camForward: new THREE.Vector3(0, 0, -1),
      floor: new THREE.Vector3(1, 0, 0.28),
      atlasTexel: this.atlasTexel,
      decalAtlasTexel: this.atlasTexel,
      blueNoise: this.textures.blueNoise,
      viewSpace: false,
      floorY: 0,
    };

    this.ambience = {
      enabled: true,
      motes: 0.55,       // particles/second multiplier
      embers: 0,
      grit: 0,
      radius: 7,
      _acc: { motes: 0, embers: 0, grit: 0 },
    };

    this._gatherLighting(ctx);
    this._buildAPI(ctx);
    this._wireEvents(ctx);
    this._registerShots(ctx);
  }

  /* ---------------------------------------------------------------- *
   * lighting feature-detection
   * ---------------------------------------------------------------- */
  _gatherLighting(ctx) {
    const e = this.env;
    const INV_PI = 1 / Math.PI;

    let sun = ctx.lighting?.sun || null;
    let hemi = null;
    let amb = null;
    if (!sun || !hemi) {
      ctx.scene.traverse((o) => {
        if (o.userData?.fx) return;
        if (!sun && o.isDirectionalLight) sun = o;
        if (!hemi && o.isHemisphereLight) hemi = o;
        if (!amb && o.isAmbientLight) amb = o;
      });
    }

    if (ctx.sky?.sunDirection) e.sunDirWorld.copy(ctx.sky.sunDirection).normalize();
    else if (sun) e.sunDirWorld.copy(sun.position).sub(sun.target?.position || _v.set(0, 0, 0)).normalize();

    if (sun) e.sunColor.copy(sun.color).multiplyScalar(sun.intensity * INV_PI);
    else e.sunColor.setRGB(1.0, 0.94, 0.86);

    if (hemi) {
      e.ambSky.copy(hemi.color).multiplyScalar(hemi.intensity * INV_PI);
      e.ambGround.copy(hemi.groundColor).multiplyScalar(hemi.intensity * INV_PI);
    } else {
      e.ambSky.setRGB(0.18, 0.22, 0.27);
      e.ambGround.setRGB(0.07, 0.06, 0.05);
    }
    if (amb) {
      _col.copy(amb.color).multiplyScalar(amb.intensity * INV_PI);
      e.ambSky.add(_col);
      e.ambGround.add(_col);
    }

    // world floor fallback for off-screen particle collision
    const bounds = ctx.level?.navBounds || ctx.level?.bounds;
    e.floor.set(1, bounds?.min?.y !== undefined ? bounds.min.y : 0, 0.28);
    e.floorY = e.floor.y;
  }

  /* ---------------------------------------------------------------- *
   * frozen ctx.fx API
   * ---------------------------------------------------------------- */
  _buildAPI(ctx) {
    const self = this;
    ctx.fx = {
      impact: (point, normal, surface, opts) => self.impact(point, normal, surface, opts),
      tracer: (from, to, opts) => self.tracer(from, to, opts),
      muzzleFlash: (matrixWorld, opts) => self.muzzleFlash(matrixWorld, opts),
      explosion: (point, radius, power) => self.explosion(point, radius, power),
      smoke: (point, opts) => self.smoke(point, opts),
      blood: (point, normal, opts) => self.blood(point, normal, opts),
      decal: (point, normal, surface, opts) => self.decal(point, normal, surface, opts),
      shell: (matrixWorld, opts) => self.shell(matrixWorld, opts),

      /* --- extensions (additive; nothing frozen is removed) --- */
      lightShaft: (o) => self.addLightShaft(o),
      clearLightShafts: () => self.clearLightShafts(),
      ambience: self.ambience,
      shockwaves: self.shockwaves,
      shockwaveUniforms: () => self.shockwaveUniforms(),
      reset: () => self.reset(),
      stats: () => self.stats(),
      depthNormalTexture: () => self.depth.texture,
      system: self,

      /* --- numeric verification hooks used by tools/ --- */
      __debug: {
        stats: () => self.stats(),
        decalStats: () => self.decals.densityStats(),
        /**
         * Simulate a firefight's worth of rounds into one patch of ground and
         * report what the density cap left behind. `n` rounds inside a
         * `extent` metre square is roughly what `ai_squad_engaged` produces.
         */
        decalStorm(n = 400, extent = 4, surface = 'concrete') {
          const rng = makeRng(0xB1A57);
          const up = new THREE.Vector3(0, 1, 0);
          const p = new THREE.Vector3();
          const base = self.stageAnchor || ctx.camera.position;
          for (let i = 0; i < n; i++) {
            p.set(base.x + (rng() - 0.5) * extent, base.y + 0.01, base.z + (rng() - 0.5) * extent);
            self.decal(p, up, surface);
          }
          return self.decals.densityStats();
        },
        /** Every particle type's shading model, so nothing is silently unlit. */
        typeShading() {
          const out = {};
          for (const [k, t] of Object.entries(TYPES)) {
            out[k] = {
              pool: t.pool === POOL.ADD ? 'additive' : 'alpha',
              media: t.media ?? 0, light: t.light, softDepth: t.soft,
              sizeJitter: t.sizeJitter, rotSpeed: t.rotSpeed,
            };
          }
          return out;
        },
      },
    };
  }

  /* ---------------------------------------------------------------- *
   * event bus
   * ---------------------------------------------------------------- */
  _wireEvents(ctx) {
    const ev = ctx.events;
    if (!ev) return;
    this._off = [
      ev.on('ballistics:hit', (p) => {
        if (!p || !p.point) return;
        const n = p.normal || _n.set(0, 1, 0);
        const surface = p.surface || p.entity?.userData?.surface || DEFAULT_SURFACE;
        this.impact(p.point, n, surface, { dir: p.dir, penetrated: p.penetrated });
      }),
      ev.on('ballistics:tracer', (p) => {
        if (!p || !p.from || !p.to) return;
        this.tracer(p.from, p.to, { speed: p.speed });
      }),
      ev.on('weapon:fire', (p) => {
        if (!p) return;
        const seed = (p.seed ?? this.rng.muzzle()) >>> 0;
        this._fireFromEvent(p, seed);
      }),
      ev.on('player:footstep', (p) => {
        if (!p || !p.position) return;
        this.footstep(p.position, p.surface || DEFAULT_SURFACE);
      }),
      ev.on('player:land', (p) => {
        if (!p || !p.position) return;
        this.land(p.position, p.impact ?? 1);
      }),
      ev.on('fx:explosion', (p) => {
        if (!p || !p.position) return;
        this.explosion(p.position, p.radius ?? 4, p.power ?? 1);
      }),
      ev.on('damage:dealt', (p) => {
        if (!p || !p.entity) return;
        const pos = p.entity.position || p.entity.getWorldPosition?.(_v);
        if (!pos) return;
        _v2.copy(this.ctx.camera.position).sub(pos).normalize();
        this.blood(pos, _v2, { power: p.headshot ? 1.8 : 1.0 });
      }),
    ];
  }

  _fireFromEvent(p, seed) {
    if (!p.origin || !p.dir) return;
    const origin = new THREE.Vector3().copy(p.origin);
    const dir = new THREE.Vector3().copy(p.dir).normalize();
    const rng = makeRng(seed);
    const vm = this.ctx.weapons?.viewmodelRoot;
    this.muzzleFlashAt(origin, dir, {
      seed: rng(),
      space: vm && vm.visible !== false ? 'view' : 'world',
      worldPos: origin,
    });
    // eject a case to the shooter's right
    const right = new THREE.Vector3(0, 1, 0).cross(dir);
    if (right.lengthSq() < 1e-4) right.set(1, 0, 0);
    right.normalize();
    const ejectDir = right.clone().addScaledVector(_UP, 0.75).normalize();
    const ejectPos = origin.clone().addScaledVector(right, -0.03).addScaledVector(dir, -0.12);
    this.shells.eject(ejectPos, ejectDir, { seed: rng(), speed: 2.2 + rng() * 1.4 });
  }

  /* ---------------------------------------------------------------- *
   * spawning helpers
   * ---------------------------------------------------------------- */
  _burst(burst, point, normal, incoming, rng, scale = 1, inheritVel = null) {
    const type = TYPE_INDEX[burst.type];
    if (type === undefined) return;
    const pool = this._poolFor(burst.type);
    const count = Math.max(1, Math.round(burst.count * this.qscale * scale));

    // emission axis
    const _n = _bN.copy(normal);
    if (_n.lengthSq() < 1e-6) _n.set(0, 1, 0);
    _n.normalize();
    const ax = _bAx;
    if (burst.axis === 'up') ax.set(0, 1, 0);
    else if (burst.axis === 'r' && incoming) ax.copy(incoming).reflect(_n).normalize();
    else if (burst.axis === 'nr' && incoming) ax.copy(incoming).reflect(_n).add(_n).normalize();
    else ax.copy(_n);
    if (ax.lengthSq() < 1e-6) ax.copy(_n);
    ax.normalize();

    // orthonormal basis around `ax`
    if (Math.abs(ax.y) > 0.95) _bTan.set(1, 0, 0); else _bTan.set(0, 1, 0);
    _bBit.crossVectors(_bTan, ax).normalize();
    _bTan.crossVectors(ax, _bBit);

    const cosMax = Math.cos(Math.min(Math.PI, burst.spread));
    const [s0, s1] = burst.speed;
    const [l0, l1] = burst.life;
    const [z0, z1] = burst.size;

    for (let i = 0; i < count; i++) {
      const cz = 1 - rng() * (1 - cosMax);
      const sz = Math.sqrt(Math.max(0, 1 - cz * cz));
      const phi = rng() * Math.PI * 2;
      const d = _bDir.copy(ax).multiplyScalar(cz)
        .addScaledVector(_bBit, Math.cos(phi) * sz)
        .addScaledVector(_bTan, Math.sin(phi) * sz);
      // heavy tail on speed so a few pieces really fly
      const speed = s0 + (s1 - s0) * Math.pow(rng(), 1.7);
      const life = l0 + (l1 - l0) * rng();
      const size = z0 + (z1 - z0) * Math.pow(rng(), 1.4);
      const jr = burst.radius;
      const px = point.x + _n.x * burst.offset + (rng() - 0.5) * jr * 2;
      const py = point.y + _n.y * burst.offset + (rng() - 0.5) * jr * 2;
      const pz = point.z + _n.z * burst.offset + (rng() - 0.5) * jr * 2;
      let vx = d.x * speed, vy = d.y * speed, vz = d.z * speed;
      if (burst.tangent) {
        vx += _bBit.x * (rng() - 0.5) * burst.tangent;
        vy += _bBit.y * (rng() - 0.5) * burst.tangent;
        vz += _bBit.z * (rng() - 0.5) * burst.tangent;
      }
      if (inheritVel && burst.inherit) {
        vx += inheritVel.x * burst.inherit;
        vy += inheritVel.y * burst.inherit;
        vz += inheritVel.z * burst.inherit;
      }
      pool.spawn(px, py, pz, vx, vy, vz, life, size, type, rng(), rng() * 6.2832);
    }
  }

  _poolFor(typeName) {
    return this._addTypes.has(typeName) ? this.addPool : this.alphaPool;
  }

  /* ---------------------------------------------------------------- *
   * public effects
   * ---------------------------------------------------------------- */
  impact(point, normal, surface = DEFAULT_SURFACE, opts = {}) {
    if (!this.enabled) return;
    const recipe = RECIPES[surface] || RECIPES[DEFAULT_SURFACE];
    const rng = this.rng.impact;
    const p = _pt(point);
    const nrm = _pt(normal, 1);
    const incoming = opts.dir ? _dirTmp.copy(opts.dir).normalize() : _dirTmp.copy(p).sub(this.ctx.camera.position).normalize();
    const scale = opts.scale ?? 1;

    for (const b of recipe.bursts) this._burst(b, p, nrm, incoming, rng, scale);

    if (recipe.decal && opts.decal !== false) this.decal(p, nrm, surface, opts);

    if (recipe.light) {
      const L = recipe.light;
      _v.copy(p).addScaledVector(nrm, 0.12);
      this.lights.flash(_v, L.color, L.intensity * scale, L.radius, L.time);
    }
    if (surface === 'flesh') this._bloodDecalCast(p, incoming, rng);
  }

  blood(point, normal, opts = {}) {
    const p = _pt(point);
    const n = _pt(normal, 1);
    const rng = this.rng.impact;
    const power = opts.power ?? 1;
    for (const b of RECIPES.flesh.bursts) this._burst(b, p, n, _dirTmp.copy(n).negate(), rng, power);
    this._bloodDecalCast(p, _dirTmp.copy(n).negate(), rng);
  }

  /** Cast along the spray direction and paint blood on whatever it lands on. */
  _bloodDecalCast(point, incoming, rng) {
    const phys = this.ctx.physics;
    if (!phys?.raycast) return;
    for (let i = 0; i < 3; i++) {
      _v.copy(incoming);
      _v.x += (rng() - 0.5) * 0.55; _v.y += (rng() - 0.5) * 0.35; _v.z += (rng() - 0.5) * 0.55;
      _v.normalize();
      const hit = this._ray(_v2.copy(point).addScaledVector(_v, 0.05), _v, 3.2);
      if (!hit) continue;
      this.decals.add(hit.point, hit.normal, {
        tile: rng() < 0.5 ? DECAL_TILE.bloodA : DECAL_TILE.bloodB,
        size: 0.24 + rng() * 0.5,
        depth: 0.08,
        tint: [0.32, 0.045, 0.032],
        life: 120,
        roll: rng() * 6.2832,
        normalStrength: 0.15,
      });
    }
  }

  decal(point, normal, surface = DEFAULT_SURFACE, opts = {}) {
    const recipe = RECIPES[surface] || RECIPES[DEFAULT_SURFACE];
    const d = opts.decalDef || recipe.decal;
    if (!d) return;
    const rng = this.rng.decal;
    const size = opts.size ?? (d.size[0] + (d.size[1] - d.size[0]) * rng());
    this.decals.add(_pt(point), _pt(normal, 1), {
      tile: opts.tile ?? d.tile,
      size,
      sizeY: size * (0.86 + rng() * 0.28),
      depth: opts.depth ?? d.depth,
      tint: opts.tint || d.tint,
      life: opts.life ?? d.life,
      roll: opts.roll ?? rng() * 6.2832,
      normalStrength: opts.normalStrength ?? 1,
      tintStrength: opts.tintStrength ?? 1,
    });
  }

  tracer(from, to, opts = {}) {
    const rng = this.rng.shot;
    if (opts.always !== true && (opts.fraction ?? 0.28) < rng()) return;
    this.tracers.add(_pt(from), _pt(to, 1), {
      speed: opts.speed ?? 780,
      width: opts.width ?? 0.045,
      length: opts.length ?? 2.4,
    });
  }

  /** @param {THREE.Matrix4} matrixWorld muzzle transform: origin at the crown, -Z down the bore */
  muzzleFlash(matrixWorld, opts = {}) {
    if (matrixWorld && matrixWorld.isMatrix4) {
      _v.setFromMatrixPosition(matrixWorld);
      _m3.setFromMatrix4(matrixWorld);
      _v2.set(0, 0, -1).applyMatrix3(_m3).normalize();
      if (opts.axis === '+z') _v2.negate();
      return this.muzzleFlashAt(_v, _v2, opts);
    }
    if (matrixWorld && matrixWorld.isVector3) {
      return this.muzzleFlashAt(matrixWorld, opts.dir || _v2.set(0, 0, -1), opts);
    }
    return null;
  }

  muzzleFlashAt(pos, dir, opts = {}) {
    const rng = this.rng.muzzle;
    const seed = opts.seed ?? rng();
    const p = _pt(pos);
    const d = _pt(dir, 1).normalize();
    const size = (opts.size ?? 0.30) * (0.78 + seed * 0.5);
    const intensity = opts.intensity ?? 1;

    const space = opts.space || (this.ctx.weapons?.viewmodelRoot ? 'view' : 'world');
    const flash = space === 'view' ? this.muzzleView : this.muzzleWorld;
    const slot = flash.fire(p, d, {
      size, intensity, seed,
      life: opts.life ?? 0.048,
      hold: opts.hold ?? 0,
    });

    // real light on the world, always in world space
    const wp = opts.worldPos ? _pt(opts.worldPos) : p;
    // Muzzle light. Same story as the bay practicals: 26 was chosen against an
    // over-exposed frame and now clips a 3 m radius of wall to pure white.
    this.lights.flash(_v3.copy(wp).addScaledVector(d, 0.18),
      [1.0, 0.72, 0.36], 13 * intensity, 9.0, opts.lightTime ?? 0.042);
    if (space === 'view') {
      this.viewLights.flash(_v3.copy(p).addScaledVector(d, 0.10),
        [1.0, 0.74, 0.40], 4.5 * intensity, 2.2, opts.lightTime ?? 0.042);
    }

    // gas, smoke wisps and a few embers into the world pools
    const wdir = _v2.copy(d);
    const wpos = _v.copy(wp);
    this._spawnMany(this.addPool, 'gas_puff', Math.round(5 * this.qscale), rng, (i, r) => ({
      p: [wpos.x + wdir.x * (0.06 + i * 0.045), wpos.y + wdir.y * (0.06 + i * 0.045) + (r() - 0.5) * 0.02,
          wpos.z + wdir.z * (0.06 + i * 0.045)],
      v: [wdir.x * (5 + r() * 7) + (r() - 0.5) * 2.0,
          wdir.y * (5 + r() * 7) + (r() - 0.5) * 2.0,
          wdir.z * (5 + r() * 7) + (r() - 0.5) * 2.0],
      life: 0.10 + r() * 0.10, size: 0.10 + r() * 0.10,
    }));
    this._spawnMany(this.alphaPool, 'smoke_muzzle', Math.round(9 * this.qscale), rng, (i, r) => ({
      p: [wpos.x + wdir.x * (0.05 + r() * 0.35), wpos.y + wdir.y * (0.05 + r() * 0.35) + (r() - 0.5) * 0.05,
          wpos.z + wdir.z * (0.05 + r() * 0.35)],
      v: [wdir.x * (1.6 + r() * 5.0) + (r() - 0.5) * 1.6,
          wdir.y * (1.6 + r() * 5.0) + (r() - 0.5) * 1.6 + 0.3,
          wdir.z * (1.6 + r() * 5.0) + (r() - 0.5) * 1.6],
      life: 0.55 + r() * 0.85, size: 0.05 + r() * 0.09,
    }));
    this._spawnMany(this.addPool, 'ember', Math.round(6 * this.qscale), rng, (i, r) => ({
      p: [wpos.x + wdir.x * 0.05, wpos.y + wdir.y * 0.05, wpos.z + wdir.z * 0.05],
      v: [wdir.x * (3 + r() * 9) + (r() - 0.5) * 3.5,
          wdir.y * (3 + r() * 9) + (r() - 0.5) * 3.5,
          wdir.z * (3 + r() * 9) + (r() - 0.5) * 3.5],
      life: 0.22 + r() * 0.5, size: 0.010 + r() * 0.016,
    }));

    if (this.ctx.render?.flash) {
      try { this.ctx.render.flash([1.0, 0.8, 0.55], 0.10 * intensity, 0.05); } catch { /* stub */ }
    }
    return slot;
  }

  _spawnMany(pool, typeName, count, rng, fn) {
    const type = TYPE_INDEX[typeName];
    if (type === undefined) return;
    for (let i = 0; i < count; i++) {
      const o = fn(i, rng);
      pool.spawn(o.p[0], o.p[1], o.p[2], o.v[0], o.v[1], o.v[2],
        o.life, o.size, type, rng(), rng() * 6.2832);
    }
  }

  shell(matrixWorld, opts = {}) {
    const rng = this.rng.shell;
    if (matrixWorld?.isMatrix4) {
      _v.setFromMatrixPosition(matrixWorld);
      _m3.setFromMatrix4(matrixWorld);
      _v2.set(1, 0.6, 0).applyMatrix3(_m3).normalize();
    } else if (matrixWorld?.isVector3) {
      _v.copy(matrixWorld);
      _v2.copy(opts.dir || _v3.set(1, 0.7, 0)).normalize();
    } else return;
    this.shells.eject(_v, _v2, { seed: rng(), speed: opts.speed ?? 2.6, inherit: opts.inherit });
  }

  /**
   * @param {object} opts { count, radius, rise, size, life, type, spread }
   *   `spread` scales the lateral velocity. Below 1 the puffs stay together and
   *   read as one rising column; at 1 they disperse into separate sprites.
   */
  smoke(point, opts = {}) {
    const rng = this.rng.impact;
    const p = _pt(point);
    const count = Math.round((opts.count ?? 42) * this.qscale);
    const radius = opts.radius ?? 0.5;
    const rise = opts.rise ?? 1.4;
    const size = opts.size ?? 0.55;
    const life = opts.life ?? 5.5;
    const lat = opts.spread ?? 1;
    const type = TYPE_INDEX[opts.type || 'smoke_thick'];
    for (let i = 0; i < count; i++) {
      const th = rng() * Math.PI * 2;
      const ph = Math.acos(1 - 2 * rng());
      const r = radius * Math.pow(rng(), 0.42);
      const dx = Math.sin(ph) * Math.cos(th), dy = Math.cos(ph) * 0.65 + 0.35, dz = Math.sin(ph) * Math.sin(th);
      this.alphaPool.spawn(
        p.x + dx * r, p.y + Math.abs(dy) * r * 0.9, p.z + dz * r,
        dx * (0.4 + rng() * 1.2) * lat, dy * rise * (0.4 + rng()), dz * (0.4 + rng() * 1.2) * lat,
        life * (0.6 + rng() * 0.8), size * (0.45 + rng() * 1.1),
        type, rng(), rng() * 6.2832);
    }
  }

  explosion(point, radius = 4, power = 1) {
    const p = _pt(point);
    const rng = this.rng.explode;
    const s = Math.max(0.35, radius / 4) * (0.7 + power * 0.3);

    // fireball
    this._spawnMany(this.addPool, 'fire_core', Math.round(16 * this.qscale), rng, (i, r) => {
      const d = _sphere(r);
      return {
        p: [p.x + d.x * 0.25 * s, p.y + d.y * 0.25 * s + 0.15 * s, p.z + d.z * 0.25 * s],
        v: [d.x * (2 + r() * 9) * s, Math.abs(d.y) * (3 + r() * 8) * s + 1.5 * s, d.z * (2 + r() * 9) * s],
        life: 0.34 + r() * 0.36, size: 0.55 * s * (0.6 + r() * 0.9),
      };
    });
    this._spawnMany(this.addPool, 'fire_edge', Math.round(24 * this.qscale), rng, (i, r) => {
      const d = _sphere(r);
      return {
        p: [p.x + d.x * 0.5 * s, p.y + Math.abs(d.y) * 0.4 * s + 0.1 * s, p.z + d.z * 0.5 * s],
        v: [d.x * (4 + r() * 14) * s, Math.abs(d.y) * (4 + r() * 12) * s + 1.0 * s, d.z * (4 + r() * 14) * s],
        life: 0.30 + r() * 0.45, size: 0.42 * s * (0.5 + r() * 1.2),
      };
    });
    // shock ring, flat on the ground
    this._spawnMany(this.addPool, 'shock_ring', 2, rng, (i, r) => ({
      p: [p.x, p.y + 0.06 + i * 0.5 * s, p.z],
      v: [0, 0.4 * s, 0],
      life: 0.32 + i * 0.12, size: 1.4 * s * (1 + i * 0.6),
    }));
    // billowing smoke
    this._spawnMany(this.alphaPool, 'smoke_thick', Math.round(54 * this.qscale), rng, (i, r) => {
      const d = _sphere(r);
      return {
        p: [p.x + d.x * 0.6 * s, p.y + Math.abs(d.y) * 0.5 * s + 0.15 * s, p.z + d.z * 0.6 * s],
        v: [d.x * (1.5 + r() * 8) * s, Math.abs(d.y) * (2 + r() * 7) * s + 1.4 * s, d.z * (1.5 + r() * 8) * s],
        life: 2.6 + r() * 3.4, size: 0.42 * s * (0.5 + r() * 1.4),
      };
    });
    // debris and embers
    this._spawnMany(this.alphaPool, 'debris_dark', Math.round(34 * this.qscale), rng, (i, r) => {
      const d = _sphere(r);
      return {
        p: [p.x + d.x * 0.3 * s, p.y + Math.abs(d.y) * 0.3 * s + 0.1, p.z + d.z * 0.3 * s],
        v: [d.x * (6 + r() * 20) * s, Math.abs(d.y) * (7 + r() * 16) * s, d.z * (6 + r() * 20) * s],
        life: 1.4 + r() * 1.8, size: 0.014 + r() * 0.045,
      };
    });
    this._spawnMany(this.addPool, 'ember', Math.round(30 * this.qscale), rng, (i, r) => {
      const d = _sphere(r);
      return {
        p: [p.x + d.x * 0.4 * s, p.y + Math.abs(d.y) * 0.35 * s + 0.1, p.z + d.z * 0.4 * s],
        v: [d.x * (3 + r() * 13) * s, Math.abs(d.y) * (4 + r() * 11) * s, d.z * (3 + r() * 13) * s],
        life: 0.8 + r() * 1.6, size: 0.012 + r() * 0.030,
      };
    });

    // dust kicked off the surfaces around the blast
    this._blastDust(p, radius, rng);

    // scorch mark underneath
    const down = this._ray(_v.copy(p), _v2.set(0, -1, 0), radius * 1.4);
    if (down) {
      this.decals.add(down.point, down.normal, {
        tile: DECAL_TILE.scorch, size: radius * 1.15, depth: 0.25,
        tint: [0.26, 0.23, 0.20], life: 120, roll: rng() * 6.2832, normalStrength: 0.1,
      });
    }

    this.lights.flash(_v.copy(p).setY(p.y + 0.4 * s),
      [1.0, 0.62, 0.24], 260 * power, radius * 7, 0.55, 'burn');

    this.shockwaves.push({
      center: p.clone(), radius: 0.05, maxRadius: radius * 3.2,
      speed: radius * 9, width: Math.max(0.35, radius * 0.28),
      strength: 0.035 * power, born: this.time,
    });

    if (this.ctx.physics?.applyRadialImpulse) {
      try { this.ctx.physics.applyRadialImpulse(p, radius, power * 18); } catch { /* stub */ }
    }
    if (this.ctx.render?.flash) {
      try { this.ctx.render.flash([1.0, 0.72, 0.4], Math.min(0.85, 0.55 * power), 0.28); } catch { /* stub */ }
    }
    if (this.ctx.render?.shake) {
      try { this.ctx.render.shake(Math.min(1.4, 0.9 * power), 0.7); } catch { /* stub */ }
    }
    if (this.ctx.audio?.play) {
      try { this.ctx.audio.play('explosion', { position: p }); } catch { /* stub */ }
    }
  }

  _blastDust(p, radius, rng) {
    const dirs = [
      [0, -1, 0], [1, -0.35, 0], [-1, -0.35, 0], [0, -0.35, 1], [0, -0.35, -1],
      [0.7, -0.2, 0.7], [-0.7, -0.2, -0.7],
    ];
    for (const d of dirs) {
      _v2.set(d[0], d[1], d[2]).normalize();
      const hit = this._ray(_v.copy(p), _v2, radius * 1.3);
      if (!hit) continue;
      const surf = hit.surface || 'concrete';
      const recipe = RECIPES[surf] || RECIPES.concrete;
      const b = recipe.bursts.find((x) => x.type.startsWith('dust')) || recipe.bursts[0];
      this._burst({ ...b, count: Math.round(b.count * 0.7), speed: [2, 7], size: [0.10, 0.30], life: [0.9, 2.2] },
        hit.point, hit.normal, _v2, rng, 1);
    }
  }

  footstep(position, surface) {
    const recipe = RECIPES[surface] || RECIPES[DEFAULT_SURFACE];
    if (!['dirt', 'sand', 'concrete'].includes(surface)) return;
    const rng = this.rng.ambient;
    const type = TYPE_INDEX[surface === 'dirt' ? 'dust_dirt' : (surface === 'sand' ? 'dust_sand' : 'dust_foot')];
    const p = _pt(position);
    const n = Math.round(5 * this.qscale);
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2, r = rng() * 0.16;
      this.alphaPool.spawn(
        p.x + Math.cos(a) * r, p.y + 0.02 + rng() * 0.05, p.z + Math.sin(a) * r,
        Math.cos(a) * (0.3 + rng() * 0.8), 0.15 + rng() * 0.5, Math.sin(a) * (0.3 + rng() * 0.8),
        0.5 + rng() * 0.7, 0.05 + rng() * 0.09, type, rng(), rng() * 6.2832);
    }
  }

  land(position, impact = 1) {
    const rng = this.rng.ambient;
    const p = _pt(position);
    const type = TYPE_INDEX.dust_foot;
    const n = Math.round(16 * this.qscale * Math.min(2, impact));
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const sp = (1.2 + rng() * 2.6) * Math.min(2, impact);
      this.alphaPool.spawn(
        p.x, p.y + 0.03, p.z,
        Math.cos(a) * sp, 0.25 + rng() * 0.6, Math.sin(a) * sp,
        0.7 + rng() * 0.9, 0.07 + rng() * 0.13, type, rng(), rng() * 6.2832);
    }
    if (this.ctx.render?.shake) {
      try { this.ctx.render.shake(0.25 * impact, 0.18); } catch { /* stub */ }
    }
  }

  /* ---------------------------------------------------------------- *
   * light shafts / ambience
   * ---------------------------------------------------------------- */
  addLightShaft(o = {}) {
    const s = new LightShaft(o);
    this.shafts.push(s);
    this.root.add(s.mesh);
    return s;
  }

  clearLightShafts() {
    for (const s of this.shafts) { s.mesh.removeFromParent(); s.dispose(); }
    this.shafts.length = 0;
  }

  _ambience(dt) {
    const A = this.ambience;
    if (!A.enabled) return;
    const rng = this.rng.ambient;
    const cam = this.ctx.camera;

    // dust motes: emitted in a box around the camera, denser inside light shafts
    A._acc.motes += dt * A.motes * 26 * this.qscale;
    const moteType = TYPE_INDEX.mote;
    while (A._acc.motes >= 1) {
      A._acc.motes -= 1;
      let px, py, pz, life, size;
      if (this.shafts.length && rng() < 0.72) {
        const sh = this.shafts[Math.floor(rng() * this.shafts.length) % this.shafts.length];
        const t = rng();
        const a = rng() * Math.PI * 2;
        const r = sh.radius * Math.sqrt(rng()) * 0.95;
        _v.copy(sh.base).addScaledVector(sh.axis, t * sh.height);
        _v2.set(0, 1, 0);
        if (Math.abs(sh.axis.y) > 0.9) _v2.set(1, 0, 0);
        _v3.crossVectors(_v2, sh.axis).normalize();
        _axis.crossVectors(sh.axis, _v3);
        _v.addScaledVector(_v3, Math.cos(a) * r).addScaledVector(_axis, Math.sin(a) * r);
        px = _v.x; py = _v.y; pz = _v.z;
        life = 4 + rng() * 6;
        size = 0.0055 + rng() * 0.012;
      } else {
        const R = A.radius;
        px = cam.position.x + (rng() - 0.5) * R * 2;
        py = cam.position.y + (rng() - 0.5) * R * 1.1;
        pz = cam.position.z + (rng() - 0.5) * R * 2;
        life = 5 + rng() * 7;
        size = 0.004 + rng() * 0.009;
      }
      this.alphaPool.spawn(px, py, pz,
        (rng() - 0.5) * 0.12, (rng() - 0.35) * 0.06, (rng() - 0.5) * 0.12,
        life, size, moteType, rng(), rng() * 6.2832, rng() * life * 0.5);
    }

    if (A.embers > 0) {
      A._acc.embers += dt * A.embers * 6 * this.qscale;
      const t = TYPE_INDEX.ember;
      while (A._acc.embers >= 1) {
        A._acc.embers -= 1;
        const R = A.radius;
        this.addPool.spawn(
          cam.position.x + (rng() - 0.5) * R * 2, cam.position.y - 1 + rng() * 2,
          cam.position.z + (rng() - 0.5) * R * 2,
          (rng() - 0.5) * 0.7, 0.2 + rng() * 0.7, (rng() - 0.5) * 0.7,
          3 + rng() * 4, 0.010 + rng() * 0.016, t, rng(), 0);
      }
    }

    if (A.grit > 0) {
      A._acc.grit += dt * A.grit * 12 * this.qscale;
      const t = TYPE_INDEX.grit;
      while (A._acc.grit >= 1) {
        A._acc.grit -= 1;
        const R = A.radius;
        this.alphaPool.spawn(
          cam.position.x + (rng() - 0.5) * R * 2, cam.position.y - 1.2 + rng() * 2.2,
          cam.position.z + (rng() - 0.5) * R * 2,
          2.5 + rng() * 3.5, (rng() - 0.4) * 0.5, (rng() - 0.5) * 1.2,
          1.6 + rng() * 2.0, 0.004 + rng() * 0.008, t, rng(), rng() * 6.28);
      }
    }
  }

  /* ---------------------------------------------------------------- *
   * shockwave handoff (render team composites this — see RENDER_HANDOFF.md)
   * ---------------------------------------------------------------- */
  shockwaveUniforms() {
    const out = [];
    for (const s of this.shockwaves) {
      out.push({
        center: s.center, radius: s.radius, width: s.width,
        strength: s.strength * Math.max(0, 1 - s.radius / s.maxRadius),
      });
    }
    return out;
  }

  _updateShockwaves(dt) {
    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const s = this.shockwaves[i];
      s.radius += s.speed * dt;
      if (s.radius > s.maxRadius) this.shockwaves.splice(i, 1);
    }
  }

  /* ---------------------------------------------------------------- *
   * raycast normalisation (physics may be a stub returning raw
   * three.js intersections)
   * ---------------------------------------------------------------- */
  _ray(origin, dir, far) {
    const phys = this.ctx.physics;
    if (!phys?.raycast) return null;
    let h = null;
    try { h = phys.raycast(origin, dir, far); } catch { return null; }
    if (!h || !h.point) return null;
    let normal = h.normal;
    if (!normal && h.face) {
      normal = _rayN.copy(h.face.normal);
      if (h.object) normal.applyNormalMatrix(_m3.getNormalMatrix(h.object.matrixWorld)).normalize();
    }
    if (!normal) normal = _rayN.set(0, 1, 0);
    if (normal.dot(dir) > 0) normal = _rayN.copy(normal).negate();
    const surface = h.surface || h.object?.userData?.surface || DEFAULT_SURFACE;
    return { point: h.point, normal, surface, object: h.object, distance: h.distance };
  }

  /* ---------------------------------------------------------------- *
   * frame
   * ---------------------------------------------------------------- */
  update(dt, ctx) {
    if (!this.enabled) return;
    const _t0 = performance.now();
    this.time += dt;
    const e = this.env;

    if (++this._lightRefresh % 45 === 0) this._gatherLighting(ctx);

    const cam = ctx.camera;
    cam.updateMatrixWorld();
    cam.matrixWorldInverse.copy(cam.matrixWorld).invert();

    const pre = ctx.render?.passes?.prepass;
    if (pre && pre.width > 1) e.resolution.set(pre.width, pre.height);
    else ctx.renderer.getDrawingBufferSize(e.resolution);
    e.camera = cam;
    e.time = this.time;
    e.atlasTexel = this.atlasTexel;
    e.decalAtlasTexel = this.atlasTexel;
    e.blueNoise = this.textures.blueNoise;
    e.camForward.set(0, 0, -1).applyQuaternion(cam.quaternion).normalize();
    e.sunDirView.copy(e.sunDirWorld).transformDirection(cam.matrixWorldInverse).normalize();
    e.upView.set(0, 1, 0).transformDirection(cam.matrixWorldInverse).normalize();

    this._ambience(dt);
    this._updateShockwaves(dt);

    // a shot preset holding a muzzle flash at peak must hold its light too
    if (this._holdLight) {
      const h = this._holdLight;
      this.lights.flash(h.pos, h.color, h.i, h.r, 0.5);
    }

    // depth + normals must be resolved before anything samples them
    this.depth.render(ctx);
    e.depthTexture = this.depth.texture;
    e.normalTexture = this.depth.normalTexture;
    e.depthMode = this.depth.mode;

    this.alphaPool.update(dt, e);
    this.addPool.update(dt, e);
    this.decals.update(dt, e);
    this.tracers.update(dt, e);
    this.muzzleWorld.update(dt, e);
    this.shells.update(dt, e);
    this.lights.update(dt);
    this.viewLights.update(dt);
    for (const s of this.shafts) s.update(e);

    e.viewSpace = true;
    this.muzzleView.update(dt, e);
    e.viewSpace = false;

    // CPU side of the FX frame. GPU cost is measured by the harness as the
    // frame-time delta with the whole system disabled — see the report.
    this._cpuMs = this._cpuMs === undefined
      ? performance.now() - _t0
      : this._cpuMs * 0.9 + (performance.now() - _t0) * 0.1;
  }

  reset() {
    this.alphaPool.reset();
    this.addPool.reset();
    this.decals.reset();
    this.tracers.reset();
    this.muzzleWorld.reset();
    this.muzzleView.reset();
    this.shells.reset();
    this.lights.reset();
    this.viewLights.reset();
    this.shockwaves.length = 0;
    this.clearLightShafts();
    this._holdLight = null;
    this.time = 0;
    for (const k of Object.keys(this.rng)) this.rng[k].reseed(RESEED[k]);
    this.ambience._acc.motes = 0;
    this.ambience._acc.embers = 0;
    this.ambience._acc.grit = 0;
    this.ambience.enabled = true;
    this.ambience.motes = 0.55;
    this.ambience.embers = 0;
    this.ambience.grit = 0;
  }

  stats() {
    return {
      drawCalls: this.alphaPool.drawCalls + this.addPool.drawCalls
        + (this.decals.mesh.visible ? 1 : 0)
        + (this.tracers.mesh.visible ? 1 : 0)
        + (this.muzzleWorld.mesh.visible ? 1 : 0)
        + (this.muzzleView.mesh.visible ? 1 : 0)
        + (this.shells.mesh.visible ? 1 : 0)
        + this.shafts.length,
      prepassOwned: !this.depth.external,
      decals: this.decals.count,
      decalDensity: this.decals.densityStats(),
      tracers: this.tracers.live,
      poolSize: this.alphaPool.size + this.addPool.size,
      cpuMs: +(this._cpuMs ?? 0).toFixed(3),
    };
  }

  resize() { /* the depth target follows the drawing buffer automatically */ }

  dispose() {
    if (this._off) for (const off of this._off) off();
    this.alphaPool.dispose();
    this.addPool.dispose();
    this.decals.dispose();
    this.tracers.dispose();
    this.muzzleWorld.dispose();
    this.muzzleView.dispose();
    this.shells.dispose();
    this.lights.dispose();
    this.viewLights.dispose();
    this.depth.dispose();
    this.clearLightShafts();
    this.textures.dispose();
    this.root.removeFromParent();
    this.viewRoot.removeFromParent();
  }

  /* ---------------------------------------------------------------- *
   * shot presets
   * ---------------------------------------------------------------- */
  /* The FX shots need a surface to shoot at that is guaranteed to exist and to
     be lit, whatever state the level is in. This builds a small self-contained
     firing bay, anchored to the real level floor and rotated so its face is
     turned into the sun, and it is only ever visible while an fx_* shot is
     being captured. */
  _stage(ctx) {
    if (this.stage) { this.stage.visible = true; return this.stage; }
    const g = new THREE.Group();
    g.name = 'fx_shot_stage';
    g.userData.fxStage = true;
    g.visible = true;

    // 1. face the sun so the target wall is key-lit, not in shadow
    const s = this.env.sunDirWorld;
    const F = new THREE.Vector3(s.x, 0, s.z);
    if (F.lengthSq() < 1e-4) F.set(0, 0, 1);
    F.normalize();
    const R = new THREE.Vector3().crossVectors(_UP, F).normalize();
    this.stageF = F.clone();
    this.stageR = R.clone();

    // 2. anchor: the nearest patch of real ground with room for the bay
    const anchor = this._findStageAnchor(ctx, F, R);
    this.stageAnchor = anchor.clone();

    const q = new THREE.Quaternion().setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(R, _UP, F));
    g.position.copy(anchor);
    g.quaternion.copy(q);

    const mk = (geo, matName, surface, pos, extraRot) => {
      let m = null;
      try { m = ctx.materials.get(matName); } catch { m = null; }
      if (!m) m = new THREE.MeshStandardMaterial({ color: 0x8a8a8a, roughness: 0.9 });
      const mesh = new THREE.Mesh(geo, m);
      mesh.position.set(pos[0], pos[1], pos[2]);
      if (extraRot) mesh.rotation.set(extraRot[0] || 0, extraRot[1] || 0, extraRot[2] || 0);
      mesh.castShadow = true; mesh.receiveShadow = true;
      mesh.userData.surface = surface;
      mesh.userData.fxStage = true;
      g.add(mesh);
      return mesh;
    };
    const box = (w, h, d) => {
      try { return chamferBox(w, h, d, 0.03); }
      catch { return new THREE.BoxGeometry(w, h, d); }
    };

    // stage-local: +x = R, +y = up, +z = F (towards the sun / the camera side)
    // a pad so the bay is grounded wherever it lands, and a parapet so the
    // silhouette never shows a floating slab edge
    mk(box(9.0, 0.22, 9.6), 'concrete_floor', 'concrete', [0, -0.11, 3.2]);
    mk(box(9.0, 0.62, 0.30), 'concrete_wall', 'concrete', [0, 0.31, 7.85]);
    mk(box(0.30, 0.62, 9.6), 'concrete_wall', 'concrete', [4.35, 0.31, 3.2]);
    mk(box(0.30, 0.62, 9.6), 'concrete_wall', 'concrete', [-4.35, 0.31, 3.2]);
    const backWall = mk(box(6.4, 3.4, 0.44), 'concrete_wall', 'concrete', [0, 1.7, -0.22]);
    const sideWall = mk(box(0.44, 3.4, 3.2), 'concrete_wall', 'concrete', [2.98, 1.7, 1.6]);
    let cyl;
    try { cyl = chamferCyl(0.30, 3.4, 22, 0.03); }
    catch { cyl = new THREE.CylinderGeometry(0.30, 0.30, 3.4, 24); }
    const pillar = mk(cyl, 'concrete_wall', 'concrete', [-1.62, 1.7, 0.52]);
    const plate = mk(box(1.15, 1.35, 0.11), 'metal_painted', 'metal', [1.35, 1.45, 0.055]);
    const board = mk(box(1.05, 1.45, 0.10), 'wood_plank', 'wood', [-0.40, 1.50, 0.05]);
    // The bay's tall pieces are targets to shoot at; for an atmospheric shot they
    // are three walls boxing the camera in. The bay is never registered with
    // physics, so a raycast sweep cannot see them either — a shot that wants a
    // clear horizon has to take them down explicitly.
    this.stageTall = [backWall, sideWall, pillar, plate, board];
    // low sandbag-ish kerb to break up the floor line
    mk(box(2.4, 0.34, 0.5), 'sandbag', 'fabric', [-2.2, 0.17, 1.9]);

    const skin = (() => {
      try { return ctx.materials.get('skin'); }
      catch { return new THREE.MeshStandardMaterial({ color: 0x9a6a55, roughness: 0.72 }); }
    })();
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.20, 0.68, 10, 20), skin);
    body.position.set(-2.95, 1.34, 1.75);
    body.castShadow = body.receiveShadow = true;
    body.userData.surface = 'flesh';
    body.userData.fxStage = true;
    g.add(body);
    this.stageBody = body;

    ctx.scene.add(g);
    g.updateMatrixWorld(true);
    this.stage = g;

    /* The bay needs to be readable whatever time of day the lighting team is
       tuning. Two local practicals — a warm key from the sun's side and a cool
       sky fill — give the shot a stable key/fill ratio without touching global
       lighting. They are point lights so nothing outside the bay is affected,
       and they stay in the scene at intensity 0 so the light count (and the
       program cache) never changes. */
    const key = new THREE.PointLight(0xffe6c2, 0, 26, 2);
    key.name = 'fx_stage_key';
    key.userData.fx = true;
    key.position.copy(this._sp(4.6, 3.2, 5.4));
    const fill = new THREE.PointLight(0x9fc4ec, 0, 20, 2);
    fill.name = 'fx_stage_fill';
    fill.userData.fx = true;
    fill.position.copy(this._sp(-2.6, 2.1, 4.2));
    ctx.scene.add(key, fill);
    this.stageKey = key;
    this.stageFill = fill;
    return g;
  }

  /** Hide any level prop that happens to sit inside the shot bay. */
  _clearBay(ctx) {
    if (this._bayHidden) { for (const o of this._bayHidden) o.visible = true; this._bayHidden = null; }
    if (!this.stage || !this.stage.visible) return;
    const a = this.stageAnchor, R = this.stageR, F = this.stageF;
    const hidden = [];
    const p = new THREE.Vector3();
    ctx.scene.traverse((o) => {
      if (!o.visible || o.userData?.fx || o.userData?.fxStage) return;
      if (!(o.isMesh || o.isInstancedMesh)) return;
      if (o === this.stage || o.parent === this.stage) return;
      // only ever hide small props fully inside the bay — never a merged batch
      const geo = o.geometry;
      if (!geo) return;
      if (!geo.boundingSphere) geo.computeBoundingSphere();
      const bs = geo.boundingSphere;
      if (!bs) return;
      const scale = Math.max(Math.abs(o.scale.x), Math.abs(o.scale.y), Math.abs(o.scale.z));
      const rad = bs.radius * scale;
      if (rad > 4.5) return;
      p.copy(bs.center).applyMatrix4(o.matrixWorld).sub(a);
      const x = p.dot(R), y = p.y, z = p.dot(F);
      if (Math.abs(x) < 4.6 + rad && y > -0.8 - rad && y < 4.6 + rad
          && z > -0.8 - rad && z < 8.6 + rad) {
        o.visible = false;
        hidden.push(o);
      }
    });
    this._bayHidden = hidden;
  }

  /**
   * Shot presets turn the bay practicals on; nothing else ever does.
   *
   * These were dialled in while the frame was running ~2 stops hot and the whole
   * scene was veiled, so they had to shout to be seen at all. With exposure
   * calibrated they are a key light, not a floodlight: at 260 the bay clipped to
   * white and threw away every shadow the impact effects are read against.
   */
  setStageLights(on) {
    if (this.stageKey) this.stageKey.intensity = on ? 90 : 0;
    if (this.stageFill) this.stageFill.intensity = on ? 7 : 0;
  }


  /** Scan the level for ground with a clear 8 x 3.6 x 7 m volume for the bay. */
  _findStageAnchor(ctx, F, R) {
    const P = ctx.physics;
    const cands = [];
    for (let x = -30; x <= 30; x += 3) {
      for (let z = -30; z <= 30; z += 3) cands.push([x, z]);
    }
    cands.sort((a, b) => (a[0] * a[0] + a[1] * a[1]) - (b[0] * b[0] + b[1] * b[1]));
    const down = new THREE.Vector3(0, -1, 0);
    const probe = new THREE.Vector3();
    let best = null;
    let nearest = null;
    for (const [x, z] of cands) {
      // start above the tallest roof — a sunlit rooftop beats a shadowed street
      const h = this._ray(probe.set(x, 30, z), down, 46);
      if (!h || h.point.y > 13.5 || h.point.y < -1.5) continue;
      const y = h.point.y;
      let clear = true;
      if (P?.pointInSolid) {
        for (let i = -3; i <= 3 && clear; i++) {
          for (let j = -2; j <= 4 && clear; j++) {
            for (const hy of [0.4, 1.7, 3.2]) {
              probe.set(x + R.x * i * 1.25 + F.x * j * 1.25,
                        y + hy,
                        z + R.z * i * 1.25 + F.z * j * 1.25);
              try { if (P.pointInSolid(probe)) { clear = false; break; } } catch { /* stub */ }
            }
          }
        }
      }
      if (!clear) continue;
      // the roof must actually be flat under the whole bay
      let flat = true;
      for (let i = -3; i <= 3 && flat; i++) {
        for (let j = -2; j <= 4 && flat; j++) {
          const hh = this._ray(probe.set(x + R.x * i * 1.25 + F.x * j * 1.25, y + 3.0,
                                         z + R.z * i * 1.25 + F.z * j * 1.25), down, 5);
          if (!hh || Math.abs(hh.point.y - y) > 0.35) flat = false;
        }
      }
      if (!flat) continue;
      if (!nearest) nearest = new THREE.Vector3(x, y + 0.02, z);
      if (!best || y > best.y) best = new THREE.Vector3(x, y + 0.02, z);
    }
    // a rooftop clears the town's long low-sun shadows; street level does not
    if (best && best.y > 3.0) return best;
    return nearest || best || new THREE.Vector3(0, 0.02, 0);
  }

  /**
   * Place the shot camera. The player system rewrites ctx.camera from its rig
   * every frame even when frozen, so a shot preset has to drive the rig itself
   * (this is the same freeze path the player team's own shots use).
   */
  _lookAt(ctx, from, at, fov) {
    look(ctx, from, at, fov);
    const ps = ctx.get?.('player');
    const rig = ps?.rig;
    if (!rig) return;
    try {
      ps.freeze?.(true);
      const e = new THREE.Euler().setFromQuaternion(ctx.camera.quaternion, 'YXZ');
      if (rig.eye?.copy) rig.eye.copy(ctx.camera.position);
      if (rig.quaternion?.copy) rig.quaternion.copy(ctx.camera.quaternion);
      if (typeof rig.fov === 'number') rig.fov = fov;
      if (typeof rig.yaw === 'number') rig.yaw = e.y;
      if (typeof rig.pitch === 'number') rig.pitch = e.x;
      if (rig.adsBlend !== undefined) rig.adsBlend = 0;
      rig.applyTo?.(ctx);
    } catch { /* player may still be a stub */ }
  }

  /** stage-local (x = right, y = up, z = out of the wall) -> world */
  _sp(x, y, z) {
    const a = this.stageAnchor, R = this.stageR, F = this.stageF;
    return new THREE.Vector3(
      a.x + R.x * x + F.x * z,
      a.y + y + F.y * z,
      a.z + R.z * x + F.z * z);
  }

  /** stage-local direction -> world */
  _sd(x, y, z) {
    const R = this.stageR, F = this.stageF;
    return new THREE.Vector3(
      R.x * x + F.x * z, y, R.z * x + F.z * z).normalize();
  }

  _shotReset(ctx, opts = {}) {
    this.reset();
    this._gatherLighting(ctx);
    this._stage(ctx);
    this.stage.visible = opts.stage !== false;
    this.setStageLights(opts.stage !== false && opts.stageLights !== false);
    this._clearBay(ctx);
    // The torso stand-in is only there for the flesh shot; in every other frame
    // it is a bare pink capsule sitting in the corner of the composition.
    if (this.stageBody) this.stageBody.visible = opts.stage !== false && opts.body === true;
    if (this.stageTall) {
      for (const m of this.stageTall) m.visible = opts.stage !== false && opts.walls !== false;
    }
    this.ambience.enabled = opts.ambience !== false;
    this.ambience.motes = opts.motes ?? 0.45;
    // the viewmodel would fill an FX close-up; shot presets may force weapon state
    const vm = ctx.weapons?.viewmodelRoot;
    if (vm) { this._vmHidden = vm.visible; vm.visible = false; }
    if (ctx.weapons) ctx.weapons.ads = 0;
    try { ctx.render?.resetHistory?.(); } catch { /* stub */ }
  }

  _registerShots(ctx) {
    const self = this;

    const impactShot = (name, surface, point, normal, camPos, camAt, fov, settle, desc) => {
      registerShot(name, (c) => {
        self._shotReset(c, { body: surface === 'flesh' });
        const p = self._sp(point[0], point[1], point[2]);
        const n = self._sd(normal[0], normal[1], normal[2]);
        const cp = self._sp(camPos[0], camPos[1], camPos[2]);
        const ca = self._sp(camAt[0], camAt[1], camAt[2]);
        const incoming = new THREE.Vector3().subVectors(p, cp).normalize();
        self.impact(p, n, surface, { dir: incoming });
        self._lookAt(c, [cp.x, cp.y, cp.z], [ca.x, ca.y, ca.z], fov);
      }, { settle, desc });
    };

    impactShot('fx_impact_concrete', 'concrete',
      [0.30, 1.78, 0.012], [0, 0, 1],
      [0.80, 1.96, 1.44], [0.29, 1.77, 0.012], 30, 6,
      'concrete impact: grey dust puff, lingering powder, chips');

    impactShot('fx_impact_metal', 'metal',
      [1.35, 1.58, 0.115], [0, 0, 1],
      [2.34, 2.02, 2.10], [1.33, 1.54, 0.115], 40, 5,
      'metal impact: hot-to-cold spark shower, ricochet trails, glow');

    impactShot('fx_impact_wood', 'wood',
      [-0.40, 1.62, 0.105], [0, 0, 1],
      [0.20, 1.90, 1.44], [-0.41, 1.61, 0.105], 30, 6,
      'wood impact: rotating splinters, lighter dust');

    impactShot('fx_impact_flesh', 'flesh',
      [-2.95, 1.52, 1.95], [0, 0.12, 1],
      [-2.18, 1.76, 3.05], [-2.96, 1.51, 1.95], 32, 5,
      'flesh impact: directional blood mist, droplets, cast-off decals');

    registerShot('fx_muzzle', (c) => {
      self._shotReset(c, { motes: 0.3 });
      const pos = self._sp(1.15, 1.62, 1.30);
      const dir = self._sd(-0.42, 0.04, -1);
      self.muzzleFlashAt(pos, dir, {
        seed: 0.37, space: 'world', size: 0.54, intensity: 1.15,
        hold: 0.17, life: 0.05,
      });
      self._holdLight = {
        pos: pos.clone().addScaledVector(dir, 0.20),
        color: [1.0, 0.72, 0.36], i: 17, r: 10,
      };
      const cp = self._sp(2.30, 1.90, 2.55);
      const ca = self._sp(0.20, 1.60, 0.35);
      self._lookAt(c, [cp.x, cp.y, cp.z], [ca.x, ca.y, ca.z], 40);
    }, { settle: 3, desc: 'muzzle flash at peak, with its point light on nearby geometry' });

    registerShot('fx_decals', (c) => {
      self._shotReset(c, { motes: 0.25 });
      const rng = makeRng(0xDECA15);
      const surfaces = ['concrete', 'concrete', 'concrete', 'concrete', 'metal', 'wood'];
      for (let i = 0; i < 32; i++) {
        const x = -2.5 + rng() * 5.0;
        const y = 0.55 + rng() * 2.15;
        const surf = surfaces[Math.floor(rng() * surfaces.length) % surfaces.length];
        self.decal(self._sp(x, y, 0.005), self._sd(0, 0, 1), surf);
      }
      // 4 rounds wrapping the pillar (curvature)
      for (let i = 0; i < 4; i++) {
        const a = -1.15 + rng() * 2.3;
        const nx = Math.sin(a), nz = Math.cos(a);
        self.decal(self._sp(-1.62 + nx * 0.30, 0.95 + rng() * 1.3, 0.52 + nz * 0.30),
          self._sd(nx, 0, nz), 'concrete');
      }
      // 4 rounds on the corner return, projecting across the 90 degree join
      for (let i = 0; i < 4; i++) {
        self.decal(self._sp(2.75, 0.95 + rng() * 1.4, 0.9 + rng() * 1.6),
          self._sd(-1, 0, 0), 'concrete');
      }
      // two fresh hits so the wall is not a museum piece
      self.impact(self._sp(-0.9, 1.72, 0.005), self._sd(0, 0, 1), 'concrete',
        { dir: self._sd(0.15, -0.2, -1) });
      self.impact(self._sp(1.35, 1.34, 0.115), self._sd(0, 0, 1), 'metal',
        { dir: self._sd(-0.15, -0.12, -1) });
      const cp = self._sp(0.55, 1.95, 3.05);
      const ca = self._sp(0.05, 1.50, 0.05);
      self._lookAt(c, [cp.x, cp.y, cp.z], [ca.x, ca.y, ca.z], 46);
    }, { settle: 16, desc: '40 rounds on a wall: decal variety, corner + curvature wrap, crater depth' });

    registerShot('fx_explosion', (c) => {
      self._shotReset(c, { motes: 0.3 });
      self.explosion(self._sp(0.3, 0.45, 4.9), 3.4, 1.2);
      const cp = self._sp(-2.9, 1.85, 0.95);
      const ca = self._sp(0.3, 1.55, 4.7);
      self._lookAt(c, [cp.x, cp.y, cp.z], [ca.x, ca.y, ca.z], 52);
    }, { settle: 16, desc: 'mid-detonation: fireball, shock ring, debris, kicked dust' });

    registerShot('fx_smoke', (c) => {
      self._shotReset(c, { motes: 0.35, walls: false });
      // The bay is anchored on whatever rooftop the level happens to offer, so a
      // hand-picked camera azimuth is a coin flip: in this map there is a taller
      // block 4.6 m off one side of it, and smoke silhouetted on a wall resolves
      // into individual sprites no matter how good the shading is. Pick the look
      // direction at shot time instead: sweep the horizon, keep the azimuths with
      // nothing in front of them, and among those take the one closest to facing
      // the sun so the column stays backlit and the light has to travel through
      // the whole depth of it to reach us.
      // The camera stays at a fixed, known-clear spot inside the bay; only the
      // azimuth is chosen at shot time.
      const eye = self._sp(0.0, 1.30, 1.60);
      // A single clear ray is not enough: it happily picks the diagonal of a
      // corner, and the frame then has a wall down each side. Require the whole
      // cone the camera will actually see to be clear, at the bottom of the frame
      // as well as the middle.
      const clearAt = (yaw, pitch, want) => {
        const h = new THREE.Vector3(Math.cos(yaw), 0, Math.sin(yaw));
        const d = h.clone().multiplyScalar(Math.cos(pitch));
        d.y = Math.sin(pitch);
        d.normalize();
        const hit = self._ray(eye.clone(), d, want + 2);
        return (hit ? hit.distance : want + 2) >= want;
      };
      let bestH = null, bestScore = -1e9;
      for (let i = 0; i < 48; i++) {
        const a = (i / 48) * Math.PI * 2;
        let ok = true;
        for (const dy of [-0.60, -0.30, 0, 0.30, 0.60]) {
          for (const pt of [0.16, 0.40, 0.75]) {
            if (!clearAt(a + dy, pt, 24)) { ok = false; break; }
          }
          if (!ok) break;
        }
        if (!ok) continue;
        const h = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
        const flat = self._ray(eye.clone(), h.clone(), 8);
        if ((flat ? flat.distance : 8) < 5.0) continue; // no room for the column
        const score = h.dot(self.stageF);               // +1 = straight into the sun
        if (score > bestScore) { bestScore = score; bestH = h; }
      }
      if (!bestH) bestH = self.stageF.clone();          // nothing is clear: take the sun

      const src = eye.clone().addScaledVector(bestH, 2.90).setY(eye.y - 1.05);
      self.smoke(src, {
        count: 340, radius: 0.50, rise: 1.70, size: 0.60, life: 16, spread: 0.30,
      });
      const ca = src.clone().addScaledVector(bestH, 0.20).setY(src.y + 1.25);
      self._lookAt(c, [eye.x, eye.y, eye.z], [ca.x, ca.y, ca.z], 46);
    }, { settle: 80, desc: 'settled smoke column backlit by the sun, showing self-shadowing' });

    registerShot('fx_ambience', (c) => {
      self._shotReset(c, { motes: 2.6 });
      self.ambience.embers = 0.4;
      self.ambience.radius = 4.5;
      const sd = self.env.sunDirWorld;
      const shaftDir = new THREE.Vector3(-sd.x, -sd.y, -sd.z).normalize();
      const a = self._sp(1.2, 3.30, 6.4);
      self.addLightShaft({
        base: a, dir: shaftDir, radius: 0.80, height: 8.0,
        density: 0.26, endTaper: 0.5, color: [1.0, 0.87, 0.64],
      });
      const b = self._sp(-1.9, 3.10, 6.4);
      self.addLightShaft({
        base: b, dir: shaftDir, radius: 0.40, height: 8.0,
        density: 0.20, endTaper: 0.45, color: [1.0, 0.85, 0.62],
      });
      const cp = self._sp(3.1, 1.70, 5.9);
      const ca = self._sp(-1.9, 1.55, 0.55);
      self._lookAt(c, [cp.x, cp.y, cp.z], [ca.x, ca.y, ca.z], 50);
    }, { settle: 90, desc: 'dust motes drifting in raking volumetric light shafts' });
  }
}


/* ------------------------------------------------------------------ *
 * small utilities
 * ------------------------------------------------------------------ */
const _ptA = new THREE.Vector3();
const _ptB = new THREE.Vector3();
const _dirTmp = new THREE.Vector3();
const _rayN = new THREE.Vector3();
const _sphTmp = new THREE.Vector3();
const _UP = new THREE.Vector3(0, 1, 0);
/* dedicated to _burst so no caller's temporary can be aliased */
const _bN = new THREE.Vector3();
const _bAx = new THREE.Vector3();
const _bTan = new THREE.Vector3();
const _bBit = new THREE.Vector3();
const _bDir = new THREE.Vector3();

function _pt(v, slot = 0) {
  const out = slot ? _ptB : _ptA;
  if (!v) return out.set(0, 0, 0);
  if (v.isVector3) return out.copy(v);
  if (Array.isArray(v)) return out.set(v[0], v[1], v[2]);
  return out.set(v.x || 0, v.y || 0, v.z || 0);
}

function _sphere(rng) {
  const z = rng() * 2 - 1;
  const t = rng() * Math.PI * 2;
  const r = Math.sqrt(Math.max(0, 1 - z * z));
  return _sphTmp.set(r * Math.cos(t), r * Math.sin(t), z);
}

const RESEED = {
  impact: 0x1a2b3c4d, muzzle: 0x000d15c0, shell: 0x00005e11,
  ambient: 0x000a3b1e, decal: 0x000deca1, explode: 0x00b0051e, shot: 0x00005407,
};
